import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import StoreOutlineIcon from 'mdi-react/StoreOutlineIcon';
import CloseCircleOutlineIcon from 'mdi-react/CloseCircleOutlineIcon';
import { Buffer } from 'buffer';
import { Input } from 'reactstrap';
import oauthService from '../../../../services/oauthService'
import Alert from '../../../../shared/components/Alert';
import { serverUrl, jsonHeader, isProdEnv } from '../../../../config';
import { platform as PLATFORM } from '../../../../shared/components/domainSupport';
import LoadingSpinner from '../../../../shared/components/widgets/LoadingSpinner';
import InvisibleRecaptcha from '../../../../shared/components/InvisibleRecaptcha';
import { history } from '../../../../helpers';

const AuthorizeMpForm = () => {
  const selectedAccount = useSelector(store => store.sellerSelect);
  const { loggedIn, user } = useSelector(store => store.authentication);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const recaptchaRef = useRef(null);
  const [platform, setPlatform] = useState(PLATFORM);
  const [accountId, setAccountId] = useState(selectedAccount.value);
  const [localAlert, setLocalAlert] = useState({});
  const [disconnectButton, setDisconnectButton] = useState(false);
  const [sellerId, setSellerId] = useState('');
  const [onboardSuccess, setOnboardSuccsss] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const mpApiParams = urlParams.get('edata');
  // const adminParam = urlParams.get('admin');

  // Parse URL params first
  let parsedAccountId = selectedAccount.value;
  let parsedPlatform = PLATFORM;

  if (mpApiParams) {
    try {
      const mpApiParamsDecoded = decodeURIComponent(mpApiParams);
      const mpApiParamsString = Buffer.from(mpApiParamsDecoded, 'base64').toString('utf-8');
      const mpApiParamsJson = JSON.parse(mpApiParamsString);
      parsedAccountId = mpApiParamsJson.accountId;
      parsedPlatform = mpApiParamsJson.platform;
    } catch (err) {
      // Error will be shown in render
    }
  }

  // Handle redirects
  if (loggedIn && window.location.pathname.startsWith('/onboarding/walmart/mp-api')) {
    history.push('/customer/onboarding/walmart/mp-api');
  }

  // Check authorization before rendering hooks
  const hasAuthError = !loggedIn && !mpApiParams;
  const hasParsingError = mpApiParams && (() => {
    try {
      const decoded = decodeURIComponent(mpApiParams);
      Buffer.from(decoded, 'base64').toString('utf-8');
      return false;
    } catch {
      return true;
    }
  })();

  // Update state from parsed params
  useEffect(() => {
    if (parsedAccountId !== selectedAccount.value) {
      setAccountId(parsedAccountId);
    }
    if (parsedPlatform !== PLATFORM) {
      setPlatform(parsedPlatform);
    }
  }, [parsedAccountId, parsedPlatform]);

  // Run on load and see if Marketplace API has already been configured and/or if it is ready to be confiugred.  Requires seller id from AD onboard.
  useEffect(() => {
    if (hasAuthError || hasParsingError) {
      return;
    }
    setLocalAlert({});
    setDisconnectButton(false);
    setLoading(true);
    setOnboardSuccsss(false);
    if (platform === 'adjuggernaut') {
      setPlatform('limitless'); // Temp workaround until all the APIs are deployed to be multi-platform.  AdRev is not live, so it's OK to onboard as-is.
    }

    const headerWithAuth = { ...jsonHeader, authorization: `JWT ${user?.jwtToken}` };
    // Add logic to check input values, make sure they are present and correct.
    const onboardOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };

    const onboardUrl = `${serverUrl}/walmart/onboard/mp-api?accountId=${selectedAccount.value || accountId}`;
    fetch(onboardUrl, onboardOptions)
      .then(async (results) => {
        if (!results.ok) {
          const text = await results.text();
          throw new Error(text);
        }
        return results.json();
      }).then((data) => {
        if (!isProdEnv) {
          console.info(data);
        }
        setSellerId(data.walmartSellerId);
        if (data.walmartMarketplaceRefreshTokenFlag) {
          setLocalAlert({ color: 'warning', message: 'Account records show that you have already onboarded your Marketpace API, only continue if: (1) you were asked to re-onboard, (2) are seeing Marketplace data issues, (3) would like to refresh your Marketplace API connection' });
          setDisconnectButton(true);
        }
        setLoading(false);
      }).catch((error) => {
        try {
          const serverMsg = JSON.parse(JSON.parse(error.message).error).error;
          setLocalAlert({ color: 'danger', message: serverMsg });
        } catch {
          setLocalAlert({ color: 'danger', message: 'Unbale to check for exsiting configuration.' });
        }
        setLoading(false);
      });
  }, [selectedAccount.value, accountId, hasAuthError, hasParsingError, platform, user]);

  if (hasAuthError) {
    return (
      <div className="form__form-group">
        <div style={{ textAlign: 'center', width: '100%' }}><CloseCircleOutlineIcon className="dashboard__x-icon" /></div>
        <br />
        <h3> You are required to be logged in or have a starting link to use the Walmart Marketplace API onboarding process.</h3>
      </div>
    );
  }

  if (hasParsingError) {
    return (
      <div className="form__form-group">
        <Alert className="INFO">You are required to be logged in or have a starting link to use the Walmart Marketplace API authorization process.</Alert>
        <br />
        Error Reference: Bad Link
      </div>
    );
  }

  const startMpOnboard = async () => {
    setSubmitted(true);
    setLocalAlert({});

    let recaptchaResponse = '';
    if (!loggedIn) {
      try {
        recaptchaResponse = await recaptchaRef.current.execute();
      } catch (err) {
        recaptchaResponse = '';
      }
    }

    const headerWithAuth = { ...jsonHeader, authorization: `JWT ${user?.jwtToken}` };
    const redirectUri = 'https://dashboard.limitless.tech/onboarding/oauth/walmart';
    const onboardStartOptions = {
      method: 'POST',
      headers: headerWithAuth,
      body: JSON.stringify({
        accountId,
        redirectUri,
        sellerId,
        platform,
        recaptcha: recaptchaResponse,
      }),
    };

    if (!recaptchaResponse && !loggedIn) {
      setLocalAlert({ color: 'warning', message: 'Please fix the input issues.' });
    } else {
      oauthService.wmMpOauth({
        options: onboardStartOptions,
        onSuccess: (data) => {
          if (!isProdEnv) {
            console.info(data);
          }
        },
        onError: (error) => {
          setLocalAlert({ color: 'danger', message: error });
        },
      });
    }
    setSubmitted(false);
  }

  const disconnectMpAPi = () => {
    setSubmitted(true);
    setLocalAlert({});

    const headerWithAuth = { ...jsonHeader, authorization: `JWT ${user?.jwtToken}` };
    const disconnectOptions = {
      method: 'POST',
      headers: headerWithAuth,
      body: JSON.stringify({
        accountId,
        sellerId,
        platform,
      }),
    };

    if (!loggedIn) {
      setLocalAlert({ color: 'warning', message: 'You must be logged in to disconnect.' });
      setSubmitted(false);
    } else {
      oauthService.wmMpDisconnect({
        options: disconnectOptions,
        onSuccess: (data) => {
          if (!isProdEnv) {
            console.info(data);
          }
          setDisconnectButton(false);
          setSubmitted(false);
        },
        onError: (error) => {
          setLocalAlert({ color: 'danger', message: JSON.stringify(error) });
          setSubmitted(false);
        },
      });
    }
  }



  return (
    <>
      {!onboardSuccess && sellerId !== '' && (
        <>
          <div className="form__form-group">
            {localAlert && localAlert.message && (
              <>
                <Alert color={localAlert.color}>{localAlert.message}</Alert>
              </>
            )}
          </div>
          <div className="form__form-group">
            <span className="form__form-group-label">Seller ID</span>
            <div className="form__form-group-field">
              <div className="form__input-group-icon">
                <StoreOutlineIcon />
              </div>
              <Input
                autoFocus
                name="sellerId"
                component="input"
                type="text"
                value={sellerId}
                onChange={(e) => setSellerId(e.target.value)}
                disabled
              />
            </div>
            {localAlert.message && (!sellerId || sellerId === '') && (
              <div className="form__form-group-error">Client ID is required</div>
            )}
          </div>
          <br />
          {!loggedIn && <InvisibleRecaptcha ref={recaptchaRef} />}
          <div style={{ textAlign: 'center', paddingBottom: '20px' }}>
            <LoadingSpinner
              active={submitted && !localAlert.message}
            />
          </div>
          {!submitted && !loading && (
            <div className="form__form-group">
              <button type="button" className="btn btn-primary account__btn" onClick={startMpOnboard} disabled={submitted}>Authorize Marketplace API</button>
            </div>
          )}
          {!submitted && !loading && disconnectButton && (
            <div className="form__form-group">
              <button type="button" className="btn btn-outline-primary account__btn" onClick={disconnectMpAPi} disabled={submitted}>Disconnect Marketplace API</button>
            </div>
          )}
        </>
      )}
      {!onboardSuccess && sellerId === '' && !loading && (
        <div className="form__form-group">
          <div style={{ textAlign: 'center', width: '100%' }}><CloseCircleOutlineIcon className="dashboard__x-icon" /></div>
          <br />
          <h3> This account has not ready for Walmart Marketplace API onboard, please work with your account manager to confirm the Advertising API has been onboarded and a Seller ID is configured correctly.</h3>
        </div>
      )}
      {loading && (
        <div style={{ textAlign: 'center', paddingBottom: '20px' }}>
          <LoadingSpinner active={loading} />
        </div>
      )}
    </>
  );
}

export default AuthorizeMpForm;
